import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft,
  Lock,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Eye,
  EyeOff,
} from "lucide-react";
import { API } from "../App";

const LOGO_URL =
  "https://static.prod-images.emergentagent.com/jobs/203f7def-ad2c-4f8f-807f-2bc10f2f16d6/images/a913d16ba95302b639f95999ec0d2a7b5d654d2a1647e0be42e9b257d09c7d8a.png";

/**
 * ResetPasswordPage — consumes `?token=...` query param, calls
 * POST /api/auth/reset-password with { token, new_password }.
 *
 * Pre-validates the token on mount via GET /api/auth/verify-reset-token/{token}
 * so we show a friendly error before the user types anything if the link is
 * already expired or used.
 */
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenReason, setTokenReason] = useState("");
  const [emailMasked, setEmailMasked] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      if (!token || token.length < 16) {
        setTokenValid(false);
        setTokenReason("invalid_or_used");
        setChecking(false);
        return;
      }
      try {
        const r = await fetch(`${API}/auth/verify-reset-token/${encodeURIComponent(token)}`);
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        setTokenValid(!!d.valid);
        setTokenReason(d.reason || "");
        setEmailMasked(d.email_masked || "");
      } catch {
        if (!cancelled) {
          setTokenValid(false);
          setTokenReason("network");
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    verify();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("A nova senha deve ter pelo menos 8 caracteres.");
      return;
    }
    if (password !== confirm) {
      toast.error("As senhas não coincidem.");
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setDone(true);
        toast.success(d.message || "Senha redefinida com sucesso!");
        // Auto-redirect after a short pause
        setTimeout(() => navigate("/login"), 2500);
      } else {
        toast.error(d.detail || "Não foi possível redefinir a senha.");
      }
    } catch {
      toast.error("Falha de conexão. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-orange-100 p-7 sm:p-8">
          <div className="flex justify-center mb-5">
            <img loading="lazy" decoding="async" src={LOGO_URL} alt="GoFood" className="h-12 w-auto" />
          </div>

          {checking ? (
            <div className="text-center py-8" data-testid="reset-checking-state">
              <Loader2 className="w-6 h-6 animate-spin text-[#FF6B00] mx-auto mb-3" />
              <p className="text-sm text-zinc-500">Validando link...</p>
            </div>
          ) : !tokenValid ? (
            <div className="text-center" data-testid="reset-invalid-state">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-7 h-7 text-red-600" />
              </div>
              <h1 className="font-heading text-xl font-extrabold text-zinc-900">
                {tokenReason === "expired" ? "Link expirado" : "Link inválido"}
              </h1>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                {tokenReason === "expired"
                  ? "Este link de recuperação expirou. Solicite um novo abaixo."
                  : "Este link de recuperação é inválido ou já foi utilizado. Solicite um novo abaixo."}
              </p>
              <div className="flex flex-col gap-2 mt-5">
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center justify-center bg-gradient-to-r from-primary to-orange-500 text-white font-semibold py-2.5 rounded-full hover:opacity-95"
                  data-testid="reset-request-new"
                >
                  Solicitar novo link
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-1 text-sm font-semibold text-zinc-600 hover:text-zinc-900"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar para o login
                </Link>
              </div>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-success-state">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="font-heading text-xl font-extrabold text-zinc-900">
                Senha redefinida!
              </h1>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                Sua senha foi alterada com sucesso. Você será redirecionado
                para o login em instantes.
              </p>
              <Link
                to="/login"
                className="inline-block mt-4 text-sm font-semibold text-[#FF6B00] hover:underline"
              >
                Entrar agora
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3">
                  <Lock className="w-5 h-5 text-[#FF6B00]" />
                </div>
                <h1 className="font-heading text-2xl font-extrabold text-zinc-900">
                  Criar nova senha
                </h1>
                {emailMasked && (
                  <p className="text-xs text-zinc-500 mt-1.5">
                    Conta: <strong className="text-zinc-700">{emailMasked}</strong>
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="password">Nova senha</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={showPwd ? "text" : "password"}
                      required
                      minLength={8}
                      placeholder="Mínimo 8 caracteres"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-10"
                      autoComplete="new-password"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((s) => !s)}
                      className="absolute inset-y-0 right-0 px-3 flex items-center text-zinc-400 hover:text-zinc-700"
                      aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm">Confirmar nova senha</Label>
                  <Input
                    id="confirm"
                    type={showPwd ? "text" : "password"}
                    required
                    minLength={8}
                    placeholder="Repita a senha"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1"
                    autoComplete="new-password"
                    data-testid="reset-confirm-input"
                  />
                </div>

                <p className="text-[11px] text-zinc-500 leading-relaxed">
                  Para sua segurança, escolha uma senha que você não use em
                  outros sites e que não seja igual à anterior.
                </p>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-primary to-orange-500 hover:from-primary/90 hover:to-orange-500/90 text-white font-semibold py-6 rounded-full"
                  disabled={loading}
                  data-testid="reset-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    "Redefinir senha"
                  )}
                </Button>
              </form>
            </>
          )}

          {!done && tokenValid && (
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-zinc-900"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Voltar para o login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
